'use client';
import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { useTranslations } from 'next-intl';
import { useMediaQuery } from '@/hooks/use-media-query';
import AnimatedCard from '@/components/ui/animated-card';
import BorderWrapper from '@/components/ui/border-wrapper';
import { motion } from 'motion/react';
import Image from 'next/image';
import Icons from '@/lib/icons';
import parse from 'html-react-parser';

interface ExternalLinkProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  websiteUrl: string;
  websiteLabel: string;
  appStoreUrl?: string;
  playStoreUrl?: string;
}

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: 'easeInOut', repeat: Infinity },
  },
} as const;

const SI = (slug: string) => `/assets/icons/${slug}-purple.svg`;

const SimpleIcon = ({ slug, alt }: { slug: string; alt: string }) => (
  <Image src={SI(slug)} alt={alt} width={18} height={18} className="w-[18px] h-[18px]" unoptimized />
);

const LinkCard = ({
  href,
  icon,
  label,
  subtitle,
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
  subtitle: string;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex items-center gap-[16px] p-[16px] md:p-[20px] rounded-[14px] border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/[0.15] transition-all duration-300"
  >
    <div className="w-[44px] h-[44px] rounded-[12px] bg-theme/15 border border-theme/20 flex items-center justify-center shrink-0 text-theme">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[15px] md:text-[16px] font-semibold leading-[1.3] text-foreground/90 group-hover:text-white transition-colors duration-300 truncate">
        {label}
      </p>
      <p className="text-[12px] font-light text-foreground/50 mt-[2px] truncate">
        {subtitle}
      </p>
    </div>
    <svg className="w-[16px] h-[16px] text-foreground/25 group-hover:text-foreground/50 transition-all duration-300 group-hover:translate-x-0.5 shrink-0" viewBox="0 0 16 16" fill="none">
      <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </a>
);

const ExternalLinkContent = ({
  websiteUrl,
  websiteLabel,
  appStoreUrl,
  playStoreUrl,
  onOpenChange,
  t,
}: {
  websiteUrl: string;
  websiteLabel: string;
  appStoreUrl?: string;
  playStoreUrl?: string;
  onOpenChange: (open: boolean) => void;
  t: ReturnType<typeof import('next-intl').useTranslations>;
}) => {
  const hasApps = appStoreUrl || playStoreUrl;

  const close = () => onOpenChange(false);

  return (
    <div className="space-y-[24px] md:space-y-[28px]">
      <p className="text-[15px] md:text-[16px] font-light leading-[1.7] text-foreground/90">
        {parse(t.raw('leavingDescription') as string)}
      </p>

      <div className="space-y-[10px]">
        <LinkCard
          href={websiteUrl}
          icon={<Icons.globe className="w-[18px] h-[18px] text-theme" />}
          label={`${websiteLabel} ${t('websiteSuffix')}`}
          subtitle={websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
        />

        {appStoreUrl && (
          <LinkCard
            href={appStoreUrl}
            icon={<SimpleIcon slug="apple" alt="App Store" />}
            label={`${websiteLabel} ${t('onAppStore')}`}
            subtitle={t('iosApp')}
            />
        )}

        {playStoreUrl && (
          <LinkCard
            href={playStoreUrl}
            icon={<SimpleIcon slug="googleplay" alt="Google Play" />}
            label={`${websiteLabel} ${t('onGooglePlay')}`}
            subtitle={t('androidApp')}
            />
        )}
      </div>

      <Button
        variant="secondary"
        className="w-full h-[48px] rounded-[14px] text-[14px] md:text-[16px] shadow-none font-light cursor-pointer"
        onClick={close}
      >
        {t('goBack')}
      </Button>
    </div>
  );
};

const ExternalLink = ({ open, onOpenChange, websiteUrl, websiteLabel, appStoreUrl, playStoreUrl }: ExternalLinkProps) => {
  const t = useTranslations('externalLink');
  const isDesktop = useMediaQuery('(min-width: 768px)');

  

  useEffect(() => {
    const lenis = typeof window !== 'undefined' ? (window as unknown as Record<string, unknown>).__lenis as { stop: () => void; start: () => void } | undefined : undefined;
    if (open) {
      lenis?.stop();
    } else {
      lenis?.start();
    }
    return () => {
      lenis?.start();
    };
  }, [open]);

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[480px] p-0 bg-transparent border-none" showCloseButton={false}>
          <AnimatedCard className="rounded-[25px]" circleClassName="w-[180px] h-[180px] blur-[100px]">
            <BorderWrapper wrapperClass="rounded-[25px]" className="rounded-[25px] bg-gradient-to-b from-[#121124] to-[#0e0821] p-[32px] lg:p-[40px] relative overflow-hidden">
              <div
                className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                />
              <div className="relative z-10">
                <DialogHeader className="text-start mb-[20px]">
                  <DialogTitle className="text-[28px] lg:text-[32px] leading-[1.1] font-semibold">
                    {t('leavingTitle')}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    {String(t.raw('leavingDescription')).replace(/<[^>]*>/g, ' ')}
                  </DialogDescription>
                </DialogHeader>
                <ExternalLinkContent
                  websiteUrl={websiteUrl}
                  websiteLabel={websiteLabel}
                  appStoreUrl={appStoreUrl}
                  playStoreUrl={playStoreUrl}
                  onOpenChange={onOpenChange}
                  t={t}
                />
              </div>
            </BorderWrapper>
          </AnimatedCard>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="flex flex-col bg-gradient-to-b from-[#121124] to-[#0e0821] border-white/10">
        <div
          className="absolute top-0 right-0 w-[180px] h-[180px] bg-theme/10 rounded-full blur-[70px] pointer-events-none animate-shape-pulse"
          />
        <DrawerHeader className="text-left pt-[24px] pb-[8px] px-[24px] relative z-10">
          <DrawerTitle className="text-[24px] leading-[1.1] font-semibold">
            {t('leavingTitle')}
          </DrawerTitle>
        </DrawerHeader>
        <div className="px-[24px] pb-[calc(24px+env(safe-area-inset-bottom))] relative z-10">
          <ExternalLinkContent
            websiteUrl={websiteUrl}
            websiteLabel={websiteLabel}
            appStoreUrl={appStoreUrl}
            playStoreUrl={playStoreUrl}
            onOpenChange={onOpenChange}
            t={t}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default ExternalLink;
